import { useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { MapPin, AlertTriangle, X } from 'lucide-react';
import { useLocationPath } from '@/hooks/useLocationNavigate';
import { Sidebar, SIDEBAR_STORAGE_KEY } from './Sidebar';
import { Header } from './Header';
import { MobileNav } from './MobileNav';
import { WalkInWizard } from '@/components/wizards';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLocationId } from '@/hooks/useLocationId';
import { useLocationContext } from '@/contexts/LocationContext';
import { trpc } from '@/trpc/client';
import { cn } from '@/lib/utils';

// Page configuration for Header
const pageConfig: Record<string, { title: string; showSearch: boolean; showActions: boolean }> = {
  '/': { title: 'Übersicht', showSearch: false, showActions: true },
  '/contacts': { title: 'Kontakte', showSearch: false, showActions: true },
  '/calendar': { title: 'Kalender', showSearch: false, showActions: true },
  '/statistics': { title: 'Statistiken', showSearch: false, showActions: true },
  '/admin': { title: 'Administration', showSearch: false, showActions: true },
  '/settings': { title: 'Einstellungen', showSearch: false, showActions: false },
};

export interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const locationPath = useLocationPath();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [walkInWizardOpen, setWalkInWizardOpen] = useState(false);

  // Sidebar pinned state - persisted in localStorage (user explicitly pinned it open)
  const [isSidebarPinned, setIsSidebarPinned] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored === 'true';
  });

  // Sidebar hover state - temporarily expands on hover
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  // Sidebar is expanded if pinned OR hovered
  const isSidebarExpanded = isSidebarPinned || isSidebarHovered;

  // Persist sidebar pinned state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isSidebarPinned));
  }, [isSidebarPinned]);

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarPinned(prev => !prev);
  }, []);

  const handleSidebarMouseEnter = useCallback(() => {
    setIsSidebarHovered(true);
  }, []);

  const handleSidebarMouseLeave = useCallback(() => {
    setIsSidebarHovered(false);
  }, []);

  const { data: currentUser } = useCurrentUser();
  const effectiveLocationId = useLocationId();
  const { isSuperAdmin, selectedLocationId } = useLocationContext();
  const { data: currentLocation } = trpc.location.getById.useQuery(
    { id: effectiveLocationId! },
    { enabled: !!effectiveLocationId }
  );
  // Banner dismissal persists for 24h per location via localStorage so it
  // doesn't re-appear on every page navigation.
  const BOOKING_BANNER_DISMISS_MS = 24 * 60 * 60 * 1000;
  const bookingBannerStorageKey = selectedLocationId
    ? `bookingBannerDismissedUntil:${selectedLocationId}`
    : null;

  const [bookingBannerDismissed, setBookingBannerDismissed] = useState(() => {
    if (!bookingBannerStorageKey) return false;
    const until = Number(localStorage.getItem(bookingBannerStorageKey));
    return Number.isFinite(until) && until > Date.now();
  });

  // Re-read storage when the selected location changes (each location has its own key)
  useEffect(() => {
    if (!bookingBannerStorageKey) {
      setBookingBannerDismissed(false);
      return;
    }
    const until = Number(localStorage.getItem(bookingBannerStorageKey));
    setBookingBannerDismissed(Number.isFinite(until) && until > Date.now());
  }, [bookingBannerStorageKey]);

  const dismissBookingBanner = () => {
    if (bookingBannerStorageKey) {
      localStorage.setItem(
        bookingBannerStorageKey,
        String(Date.now() + BOOKING_BANNER_DISMISS_MS),
      );
    }
    setBookingBannerDismissed(true);
  };

  const missingBookingUrl = !!currentLocation && !currentLocation.bookingUrl && !!selectedLocationId && !bookingBannerDismissed;

  // Super admin with no location selected → show empty state on data pages
  const showEmptyState = isSuperAdmin && !selectedLocationId;

  // Get page config based on current path (strip /location/:slug prefix)
  const currentPageConfig = useMemo(() => {
    const path = location.pathname.replace(/^\/location\/[^/]+/, '') || '/';
    // Check for exact match first, then check for prefix match
    if (pageConfig[path]) {
      return pageConfig[path];
    }
    // Check for prefix matches (e.g., /contacts/123 should match /contacts)
    for (const [route, config] of Object.entries(pageConfig)) {
      if (route !== '/' && path.startsWith(route)) {
        return config;
      }
    }
    return { title: 'vit:bikes', showSearch: false, showActions: true };
  }, [location.pathname]);

  const handleWalkInClick = useCallback(() => {
    setWalkInWizardOpen(true);
  }, []);

  const handleWalkInClose = useCallback(() => {
    setWalkInWizardOpen(false);
  }, []);

  const handleWalkInSuccess = useCallback((contactId: string, appointmentId: string) => {
    console.log('Walk-in created:', { contactId, appointmentId });
    setWalkInWizardOpen(false);
    // Could navigate to the contact or appointment here
  }, []);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar - hidden on mobile and tablet */}
      <div
        className={cn(
          "hidden md:block fixed inset-y-0 left-0 z-40 transition-all duration-300",
          isSidebarExpanded ? "w-60" : "w-20"
        )}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <Sidebar
          isExpanded={isSidebarExpanded}
          isPinned={isSidebarPinned}
          onToggle={handleSidebarToggle}
        />
      </div>

      {/* Mobile Navigation Drawer */}
      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
      />

      {/* Main Content Area - shrinks when sidebar expands (hover or pinned) */}
      <div className={cn(
        "transition-all duration-300",
        isSidebarExpanded ? "md:pl-60" : "md:pl-20"
      )}>
        {/* Header */}
        <Header
          onMenuClick={() => setIsMobileNavOpen(true)}
          onWalkInClick={handleWalkInClick}
          pageTitle={currentPageConfig.title}
          showSearch={currentPageConfig.showSearch}
          showActions={currentPageConfig.showActions}
        />

        {/* Missing booking URL warning */}
        {missingBookingUrl && (
          <div className="mx-4 lg:mx-6 mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-sm text-amber-300 flex-1">
              Kein Buchungslink für <span className="font-medium">{currentLocation?.name}</span> konfiguriert.
              {' '}Bitte unter <Link to={locationPath('/settings/location')} className="underline hover:text-amber-200">Einstellungen → Standort → Kontaktdaten</Link> hinterlegen.
            </p>
            <button
              onClick={dismissBookingBanner}
              className="shrink-0 p-1 rounded hover:bg-amber-500/20 text-amber-400 hover:text-amber-200 transition-colors"
              aria-label="Banner schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Page Content — no padding on calendar for edge-to-edge layout */}
        <main className={cn(
          location.pathname === '/calendar' ? '' : 'p-4 lg:p-6'
        )}>
          {showEmptyState && !location.pathname.includes('/settings') && !location.pathname.includes('/admin') && !location.pathname.startsWith('/superadmin') ? (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-10rem)] text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8 text-slate-500" />
              </div>
              <h2 className="text-lg font-semibold text-slate-300 mb-2">Kein Standort ausgewählt</h2>
              <p className="text-sm text-slate-500 max-w-sm">
                Wählen Sie einen Standort aus dem Dropdown oben aus, um Daten anzuzeigen.
              </p>
            </div>
          ) : (
            children
          )}
        </main>
      </div>

      {/* Walk-In Wizard — use selected location, fall back to primary */}
      {(effectiveLocationId || currentUser?.primaryLocationId) && (
        <>
          <WalkInWizard
            isOpen={walkInWizardOpen}
            onClose={handleWalkInClose}
            onSuccess={handleWalkInSuccess}
            locationId={effectiveLocationId || currentUser!.primaryLocationId}
            locationName={currentLocation?.name}
          />
        </>
      )}
    </div>
  );
}
